export type BarChartResolution = '1m' | '5m' | '10m' | '15m' | '30m' | '1h' | '1d' | '1w'

const ALL_RESOLUTIONS: BarChartResolution[] = ['1m', '5m', '10m', '15m', '30m', '1h', '1d', '1w']

const TARGET_BAR_COUNT = { min: 8, max: 24, ideal: 16 }

const RESOLUTION_MS: Record<BarChartResolution, number> = {
  '1m': 60 * 1000,
  '5m': 5 * 60 * 1000,
  '10m': 10 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
  '1w': 7 * 24 * 60 * 60 * 1000,
}

function resolutionToMs(resolution: BarChartResolution): number {
  return RESOLUTION_MS[resolution]
}

function estimateBarCount(durationMs: number, resolution: BarChartResolution): number {
  return durationMs / resolutionToMs(resolution)
}

export function getAvailableResolutions(durationMs: number): BarChartResolution[] {
  const maxWithMargin = TARGET_BAR_COUNT.max * 1.5

  const available = ALL_RESOLUTIONS.filter((resolution) => {
    const barCount = estimateBarCount(durationMs, resolution)
    return barCount >= TARGET_BAR_COUNT.min && barCount <= maxWithMargin
  })

  if (available.length > 0) return available

  if (durationMs <= resolutionToMs('1m') * TARGET_BAR_COUNT.min) {
    return ['1m']
  }

  return ['1w']
}

export function getDefaultResolution(durationMs: number): BarChartResolution {
  const available = getAvailableResolutions(durationMs)

  return available.reduce((best, current) => {
    const bestDiff = Math.abs(estimateBarCount(durationMs, best) - TARGET_BAR_COUNT.ideal)
    const currentDiff = Math.abs(estimateBarCount(durationMs, current) - TARGET_BAR_COUNT.ideal)
    return currentDiff < bestDiff ? current : best
  })
}

export function formatResolutionLabel(
  resolution: BarChartResolution,
  t: (path: string) => string,
): string {
  return t(`custom_dashboards.bar_chart.resolutions.${resolution}`)
}
